import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useProfileStore, getFacilityKeyForPortal } from "@/store/profile";
import { useTaskStore } from "@/store/task";
import taskRequest from "@/api/task-request";
import { complaintHistoryLabels as t } from "@/components/complaints/complaintHistoryLabels";
import { ChoiceGroup } from "@/components/forms/ChoiceGroup";
import { Button } from "@/components/ui/Button";
import {
  InfoTaskCompletionThankYou,
  useInfoTaskWizardCompletion,
} from "@/components/wizard/InfoTaskWizardCompletion";
import { WizardActionsBar } from "@/components/wizard/WizardActionsBar";
import { FAMILY_HISTORY_PATH, wizardPrevPath } from "@/hooks/useWizardFlow";
import { finishHistoryStep } from "@/utils/navigateToFormHandler";

const CONDITION_OPTIONS = [
  "Cancer",
  "Stroke",
  "Thyroid problems",
  "Asthma",
  "Heart attack",
  "Diabetes",
  "Nothing significant",
  "Unknown",
];

/** Choosing one of these clears every other condition (and vice versa). */
const EXCLUSIVE_OPTIONS = ["Nothing significant", "Unknown"];

const FAMILY_OPTIONS = [
  "Father",
  "Mother",
  "Brother",
  "Sister",
  "Grandfather",
  "Grandmother",
  "Son",
  "Daughter",
];

const STATUS_OPTIONS = [
  { value: "Past", label: "Past" },
  { value: "Present", label: "Present" },
];

const INCOMPLETE_ROW_ERROR = "Choose a family member and Past or Present";

/** `id` is a client-only key so a row keeps its DOM (and focus) while its fields change. */
type FamilyRow = { id: number; condition: string; family: string; status: string };

const isComplete = (r: FamilyRow) => r.family !== "" && r.status !== "";

export function FamilyHistoryPage() {
  const navigate = useNavigate();
  const currentPatient = useProfileStore((s) => s.currentPatient);
  const caseHistoryId = useTaskStore((s) => s.caseHistory?.id);
  const wizardSteps = useTaskStore((s) => s.wizardSteps);
  const { showThankYou, completeActiveInfoTask } = useInfoTaskWizardCompletion();

  // Config flow: Family History is its own wizard step. Legacy flow: the last General History sub-page.
  const isOwnStep = wizardSteps.some((s) => s.key === "family-history");

  const facilityKey = getFacilityKeyForPortal();
  const caseHistoryIdStr = caseHistoryId != null ? String(caseHistoryId) : "";

  const [isLoading, setIsLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [showRowErrors, setShowRowErrors] = useState(false);
  const [selectedConditions, setSelectedConditions] = useState<string[]>([]);
  const [familyRows, setFamilyRows] = useState<FamilyRow[]>([]);
  const [conditionToRemove, setConditionToRemove] = useState<string | null>(null);
  const nextRowId = useRef(0);

  const newRow = (condition: string, family = "", status = ""): FamilyRow => ({
    id: nextRowId.current++,
    condition,
    family,
    status,
  });

  useEffect(() => {
    if (!facilityKey || !caseHistoryIdStr) {
      setIsLoading(false);
      return;
    }
    let cancelled = false;
    taskRequest.request
      .getCaseHistoryFamilyHealthHistory(facilityKey, { caseHistoryId: caseHistoryIdStr })
      .then((res) => {
        if (cancelled) return;
        const data = (res.data as { description?: string; relation?: string; past?: boolean; present?: boolean }[]) ?? [];
        const conditions: string[] = [];
        const rows: FamilyRow[] = [];
        data.forEach((el) => {
          if (el.description && !conditions.includes(el.description)) conditions.push(el.description);
          let status = "";
          if (el.past) status = "Past";
          if (el.present) status = "Present";
          rows.push({
            id: nextRowId.current++,
            condition: el.description ?? "",
            family: el.relation ?? "",
            status,
          });
        });
        setSelectedConditions(conditions);
        setFamilyRows(rows);
      })
      .catch(() => {
        // Saving replaces the whole list, so never let a failed load turn into a save that wipes it.
        if (!cancelled) setLoadFailed(true);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [facilityKey, caseHistoryIdStr]);

  const removeCondition = (cond: string) => {
    setSelectedConditions((prev) => prev.filter((c) => c !== cond));
    setFamilyRows((prev) => prev.filter((r) => r.condition !== cond));
  };

  const handleConditionToggle = (cond: string) => {
    setSaveError(null);
    if (EXCLUSIVE_OPTIONS.includes(cond)) {
      setSelectedConditions(selectedConditions.includes(cond) ? [] : [cond]);
      setFamilyRows([]);
      return;
    }
    if (selectedConditions.includes(cond)) {
      // Ask first when deselecting would throw away family members the patient already entered.
      const hasEntries = familyRows.some((r) => r.condition === cond && (r.family || r.status));
      if (hasEntries) setConditionToRemove(cond);
      else removeCondition(cond);
      return;
    }
    setSelectedConditions([...selectedConditions.filter((c) => !EXCLUSIVE_OPTIONS.includes(c)), cond]);
    if (!familyRows.some((r) => r.condition === cond)) setFamilyRows([...familyRows, newRow(cond)]);
  };

  const confirmRemoveCondition = () => {
    if (!conditionToRemove) return;
    removeCondition(conditionToRemove);
    setConditionToRemove(null);
  };

  const addFamilyRow = (condition: string) => {
    setFamilyRows((prev) => [...prev, newRow(condition)]);
  };

  const removeFamilyRow = (id: number) => {
    setFamilyRows((prev) => prev.filter((r) => r.id !== id));
  };

  const updateFamilyRow = (id: number, field: "family" | "status", value: string) => {
    setSaveError(null);
    setFamilyRows((prev) => prev.map((r) => (r.id === id ? { ...r, [field]: value } : r)));
  };

  const goBack = () =>
    navigate(
      isOwnStep
        ? (wizardPrevPath(wizardSteps, FAMILY_HISTORY_PATH) ?? "/")
        : "/complaint/health-history-3"
    );

  const conditionsForRows = selectedConditions.filter((c) => !EXCLUSIVE_OPTIONS.includes(c));
  // Rows in the order they appear on screen, so focus lands on the topmost problem.
  const firstIncompleteRow = conditionsForRows
    .flatMap((c) => familyRows.filter((r) => r.condition === c))
    .find((r) => !isComplete(r));

  const handleSaveAndContinue = async () => {
    if (isLoading || loadFailed) return;
    if (firstIncompleteRow) {
      setShowRowErrors(true);
      setSaveError(`${INCOMPLETE_ROW_ERROR} for each condition, or tap the condition again to remove it.`);
      const el = document.getElementById(
        firstIncompleteRow.family
          ? `family-status-${firstIncompleteRow.id}`
          : `family-member-${firstIncompleteRow.id}`
      );
      el?.scrollIntoView({ block: "center", behavior: "smooth" });
      el?.focus({ preventScroll: true });
      return;
    }
    if (!facilityKey || !caseHistoryIdStr) {
      navigate("/");
      return;
    }
    setSaveError(null);
    setIsSaving(true);
    try {
      const facilityIdNum = currentPatient?.facilityID ?? 0;
      const histories = familyRows
        .filter((r) => r.condition && isComplete(r))
        .map((r) => ({
          facilityId: facilityIdNum,
          caseHistoryId: caseHistoryId!,
          id: 0,
          description: r.condition,
          relation: r.family,
          present: r.status === "Present",
          notPresent: false,
          past: r.status === "Past",
          unknown: false,
        }));
      await taskRequest.request.insertCaseHistoryFamilyHealthHistory(
        facilityKey,
        { caseHistoryId: caseHistoryId!, histories }
      );

      if (isOwnStep) {
        await finishHistoryStep(navigate, wizardSteps, FAMILY_HISTORY_PATH, "/complaint/task-complete", {
          replace: true,
        });
        return;
      }

      const completion = await completeActiveInfoTask(facilityKey);
      if (completion.kind === "failed") {
        setSaveError(t.finishFailed);
        return;
      }
      if (completion.kind === "skipped") navigate("/");
    } catch {
      setSaveError(t.finishFailed);
    } finally {
      setIsSaving(false);
    }
  };

  const selectClass =
    "block min-h-11 w-full rounded-lg border bg-white px-3 py-2 text-base font-medium text-[#1E252B] focus:outline-none focus:ring-2 focus:ring-[#2A4064]/30 focus:border-[#2A4064]";

  if (!currentPatient) {
    return (
      <div className="wizard-page w-full">
        <div className="wizard-page__content p-4">
          <p className="text-sm text-slate-600">Please sign in or select a patient.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="wizard-page w-full">
      <div className="wizard-page__content p-4">
        {showThankYou ? (
          <div className="flex min-h-[min(60vh,28rem)] flex-col items-center justify-center px-2">
            <InfoTaskCompletionThankYou />
          </div>
        ) : (
        <div className="complaint-history-card">
          <div className="complaint-history-heading">
            <div className="complaint-history-heading-bar" />
            <h2 className="complaint-history-heading-text">Family Health History</h2>
          </div>
          <div className="complaint-history-divider" />

          {isLoading ? (
            <div className="py-8">
              <div className="complaint-history-progress-track">
                <div className="complaint-history-progress-fill w-2/3" />
              </div>
            </div>
          ) : loadFailed ? (
            <div className="py-6 text-center">
              <p className="text-[15px] text-[#1E252B]" role="alert">
                We couldn't load your family health history.
              </p>
              <Button
                type="button"
                variant="outline"
                className="mt-4"
                onClick={() => window.location.reload()}
              >
                Try again
              </Button>
            </div>
          ) : (
            <>
              <ChoiceGroup
                id="family-conditions"
                multiple
                legend="Has anyone in your immediate family had any of these conditions?"
                hint="Optional. Select all that apply."
                options={CONDITION_OPTIONS.map((c) => ({ value: c, label: c }))}
                value={selectedConditions}
                onChange={(next) => {
                  // ChoiceGroup reports the whole new list; act on the one choice that changed.
                  const toggled =
                    next.find((c) => !selectedConditions.includes(c)) ??
                    selectedConditions.find((c) => !next.includes(c));
                  if (toggled) handleConditionToggle(toggled);
                }}
              />

              {conditionsForRows.length > 0 && (
                <div className="mt-6 space-y-4">
                  {conditionsForRows.map((cond) => {
                    const rows = familyRows.filter((r) => r.condition === cond);
                    return (
                      <section
                        key={cond}
                        aria-label={cond}
                        className="rounded-xl border border-[#e2e2ec] p-3"
                      >
                        <h3 className="mb-3 text-[18px] font-extrabold text-[#1E252B]">{cond}</h3>
                        <div className="space-y-4">
                          {rows.map((row, i) => {
                            const rowError = showRowErrors && !isComplete(row);
                            const memberId = `family-member-${row.id}`;
                            return (
                              <div
                                key={row.id}
                                className={
                                  i > 0 ? "space-y-3 border-t border-[#e2e2ec] pt-4" : "space-y-3"
                                }
                              >
                                <div>
                                  <label htmlFor={memberId} className="choice-group__legend block">
                                    Which family member?
                                  </label>
                                  <select
                                    id={memberId}
                                    className={`${selectClass} ${rowError && !row.family ? "border-[var(--hs-red)]" : "border-[#e2e2ec]"}`}
                                    value={row.family}
                                    aria-invalid={(rowError && !row.family) || undefined}
                                    onChange={(e) => updateFamilyRow(row.id, "family", e.target.value)}
                                  >
                                    <option value="">Select family member</option>
                                    {FAMILY_OPTIONS.map((f) => (
                                      <option key={f} value={f}>{f}</option>
                                    ))}
                                  </select>
                                </div>
                                <ChoiceGroup
                                  id={`family-status-${row.id}`}
                                  layout="segmented"
                                  legend="Is it past or present?"
                                  options={STATUS_OPTIONS}
                                  value={row.status || null}
                                  error={rowError ? INCOMPLETE_ROW_ERROR : null}
                                  onChange={(v) => updateFamilyRow(row.id, "status", v ?? "")}
                                />
                                {rows.length > 1 && (
                                  <button
                                    type="button"
                                    onClick={() => removeFamilyRow(row.id)}
                                    className="inline-flex min-h-11 items-center gap-2 rounded-lg px-2 text-[14px] font-semibold text-[#6F7375] hover:bg-slate-100 hover:text-[#1E252B] focus:outline-none focus:ring-2 focus:ring-[#2A4064]/30"
                                    aria-label={`Remove ${row.family || "family member"} for ${cond}`}
                                  >
                                    <i className="fas fa-trash-alt text-sm" aria-hidden />
                                    Remove
                                  </button>
                                )}
                              </div>
                            );
                          })}
                        </div>
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => addFamilyRow(cond)}
                          className="mt-4 min-h-11 w-full"
                        >
                          <i className="fas fa-plus mr-2 text-sm" aria-hidden />
                          Add another family member
                        </Button>
                      </section>
                    );
                  })}
                </div>
              )}
            </>
          )}
          {saveError && (
            <p className="choice-group__error mt-4" role="alert">
              {saveError}
            </p>
          )}
        </div>
        )}
      </div>
      {!showThankYou && (
        <WizardActionsBar>
          <Button type="button" variant="outline" onClick={goBack}>
            {t.backButton}
          </Button>
          <Button
            type="button"
            onClick={handleSaveAndContinue}
            disabled={isSaving || isLoading || loadFailed}
          >
            {isSaving ? "Saving…" : t.continueButton}
          </Button>
        </WizardActionsBar>
      )}

      {conditionToRemove && (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 p-4 sm:items-center"
          role="dialog"
          aria-modal="true"
          aria-labelledby="family-remove-confirm-title"
          onKeyDown={(e) => {
            if (e.key === "Escape") setConditionToRemove(null);
          }}
        >
          <div className="w-full max-w-sm rounded-xl bg-white p-6 shadow-lg">
            <h3 id="family-remove-confirm-title" className="text-[16px] font-bold text-[#1E252B]">
              Remove {conditionToRemove}?
            </h3>
            <p className="mt-2 text-[14px] text-[#6F7375]">
              The family members you added for {conditionToRemove} will be removed.
            </p>
            <div className="mt-5 flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                autoFocus
                onClick={() => setConditionToRemove(null)}
              >
                Cancel
              </Button>
              <Button type="button" onClick={confirmRemoveCondition}>
                Remove
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
